import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { DataLinkAccount } from '../hooks/useDataLinkAccounts.ts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { Button } from '../components/ui/button.tsx';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import { Switch } from '../components/ui/switch.tsx';
import { useDataLinkAccounts } from '../hooks/useDataLinkAccounts.ts';
import { useDataLinkOnboarding } from '../hooks/useDataLinkOnboarding.ts';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { usePermissions } from '../hooks/usePermissions.ts';
import { createLogger, Permission } from '@metron/shared';
import { CreateDataLinkAccountModal } from '../components/CreateDataLinkAccountModal.tsx';
import { ResetDataLinkCredentialsDialog } from '../components/ResetDataLinkCredentialsDialog.tsx';
import { DeleteDataLinkAccountDialog } from '../components/DeleteDataLinkAccountDialog.tsx';
import { KeyRound, Plus, Trash2 } from 'lucide-react';

const logger = createLogger('DataLinkAccountsPage');

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

function ExternalAccessToggle({
  account,
  canManage,
  onToggle,
}: {
  account: DataLinkAccount;
  canManage: boolean;
  onToggle: (username: string, enabled: boolean) => Promise<unknown>;
}) {
  const [isPending, setIsPending] = useState(false);

  const handleToggle = async (checked: boolean) => {
    setIsPending(true);
    try {
      await onToggle(account.username, checked);
    } catch (err) {
      logger.error({ err }, 'Failed to toggle external access');
    } finally {
      setIsPending(false);
    }
  };

  if (!canManage) {
    return account.externalEnabled
      ? <span className='text-green-600 dark:text-green-400'>Yes</span>
      : <span className='text-muted-foreground'>No</span>;
  }

  return (
    <Switch
      checked={account.externalEnabled}
      onCheckedChange={handleToggle}
      disabled={isPending}
      aria-label={`Toggle external API access for ${account.email}`}
    />
  );
}

export function DataLinkAccountsPage() {
  const {
    accounts,
    isLoading,
    error,
    refetch,
    updateExternalAccess,
    resetCredentials,
    deleteAccount,
  } = useDataLinkAccounts();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.MANAGE_DATALINK_ACCOUNTS);
  const [resetAccount, setResetAccount] = useState<DataLinkAccount | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DataLinkAccount | null>(null);

  const columns: ColumnDef<DataLinkAccount>[] = [
    {
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Email' />,
      cell: ({ row }) => <div className='font-medium'>{row.getValue('email')}</div>,
    },
    {
      accessorKey: 'customerName',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
      cell: ({ row }) => {
        const value = row.getValue('customerName') as string | undefined;
        return value || <span className='text-muted-foreground'>Unknown</span>;
      },
    },
    {
      accessorKey: 'customerId',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Customer ID' />,
      cell: ({ row }) => <code className='text-xs'>{row.getValue('customerId')}</code>,
    },
    {
      accessorKey: 'userStatus',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
      cell: ({ row }) => {
        const status = row.getValue('userStatus') as string;
        const statusColors: Record<string, string> = {
          CONFIRMED: 'text-green-600 dark:text-green-400',
          FORCE_CHANGE_PASSWORD: 'text-yellow-600 dark:text-yellow-400',
          UNCONFIRMED: 'text-gray-600 dark:text-gray-400',
        };
        return (
          <span className={statusColors[status] || 'text-muted-foreground'}>
            {status.replace(/_/g, ' ')}
          </span>
        );
      },
    },
    {
      accessorKey: 'enabled',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Enabled' />,
      cell: ({ row }) => {
        const enabled = row.getValue('enabled') as boolean;
        return enabled
          ? <span className='text-green-600 dark:text-green-400'>Yes</span>
          : <span className='text-destructive'>No</span>;
      },
    },
    {
      accessorKey: 'externalEnabled',
      header: ({ column }) => <DataTableColumnHeader column={column} title='External API' />,
      cell: ({ row }) => {
        const account = row.original;
        return (
          <ExternalAccessToggle
            account={account}
            canManage={canManage}
            onToggle={updateExternalAccess}
          />
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Created At' />,
      cell: ({ row }) => (
        <span className='text-sm text-muted-foreground'>
          {formatDate(row.getValue('createdAt'))}
        </span>
      ),
    },
    ...(canManage
      ? [
        {
          id: 'actions',
          header: () => <span className='sr-only'>Actions</span>,
          cell: ({ row }: { row: { original: DataLinkAccount } }) => (
            <div className='flex items-center gap-1'>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setResetAccount(row.original)}
                title={`Reset credentials for ${row.original.email}`}
              >
                <KeyRound className='mr-2 h-4 w-4' />
                Reset
              </Button>
              <Button
                variant='ghost'
                size='sm'
                className='text-destructive hover:text-destructive'
                onClick={() => setDeleteTarget(row.original)}
                title={`Delete account ${row.original.email}`}
              >
                <Trash2 className='mr-2 h-4 w-4' />
                Delete
              </Button>
            </div>
          ),
        } satisfies ColumnDef<DataLinkAccount>,
      ]
      : []),
  ];
  const { onboardCustomer } = useDataLinkOnboarding();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const tableState = useTableUrlState();

  const handleCreateAccount = async (data: { email: string; customerId: number }) => {
    const result = await onboardCustomer(data);
    // Refetch the accounts list after successful creation
    await refetch();
    return result;
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-muted-foreground'>Loading DataLink accounts...</p>
      </div>
    );
  }

  // Only blank the page when there is nothing to show. Once accounts are
  // loaded, a failed background refetch reports inline instead of replacing
  // the tree, which would unmount any open dialog and its credentials.
  if (error && accounts.length === 0) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-destructive'>Error: {error}</p>
      </div>
    );
  }

  return (
    <>
      <div className='space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-3xl font-bold tracking-tight'>DataLink Accounts</h1>
            <p className='text-muted-foreground'>
              Manage DataLink API customer accounts
            </p>
          </div>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className='mr-2 h-4 w-4' />
            Create Account
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>DataLink Account List</CardTitle>
            <CardDescription>
              View all DataLink API customer accounts. Total: {accounts.length}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <p className='mb-4 text-sm text-destructive'>
                Could not refresh the account list: {error}
              </p>
            )}
            <DataTable
              columns={columns}
              data={accounts}
              sorting={tableState.sorting}
              onSortingChange={tableState.onSortingChange}
              pagination={tableState.pagination}
              onPaginationChange={tableState.onPaginationChange}
            />
          </CardContent>
        </Card>
      </div>

      <CreateDataLinkAccountModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onCreateAccount={handleCreateAccount}
      />

      <ResetDataLinkCredentialsDialog
        account={resetAccount}
        onOpenChange={(open) => !open && setResetAccount(null)}
        onReset={resetCredentials}
      />

      <DeleteDataLinkAccountDialog
        account={deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onDelete={deleteAccount}
      />
    </>
  );
}
